import { useState, useEffect, useRef, useCallback } from 'react';
import { contactService } from '../../api/contactService';
import { useToast } from '../../components/toast/ToastContext';
import DeletePrompt from '../../components/DeletePrompt';
import { useNavigate } from 'react-router-dom';
import { ActionType, Column, MetadataField } from '../../types/types';
import { useGetAllDynamicColumnsQuery, useGetDynamicColumnsQuery, useGetDynamicFiltersQuery, useLazyGetContactsQuery } from '../../api/contactApiSlice';
import AMSDynamicListing from '../../components/listing/AMSDynamicListing';
import { getAccessToken } from '../auth/services/auth';
import { useBulkDeleteMutation } from '../../api/searchQueryApiSlice';

interface ContactsListingProps {
  onEditContact: (id: number, onSuccess: () => void) => void;
  refreshTrigger?: number;
}

interface SavedTableParams {
  page: number;
  perPage: number;
  sort?: { field: string; direction: 'ASC' | 'DESC' };
  query?: string;
  filters: Record<string, any>;
  visibleColumns: string[];
}

const ContactsListing = ({ onEditContact, refreshTrigger }: ContactsListingProps) => {
  const STORAGE_KEY = `tableConfig_contacts`;
  const [contacts, setContacts] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filterGroups, setFilterGroups] = useState<any[]>([]);
  const initialMount = useRef(true);
  const showToast = useToast();
  const navigate = useNavigate();
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [columns, setColumns] = useState<Column[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteIds, setDeleteIds] = useState<number[]>([]);
  const [pendingCleanup, setPendingCleanup] = useState<(() => void) | null>(null);
  const [loadingExport, setLoadingExport] = useState(false);
  const [bulkDelete, { isLoading: bulkDeleteLoading }] =
    useBulkDeleteMutation();

  const [initialFilters, setInitialFilters] = useState<SavedTableParams | null>(null);
  const [shouldFetchContacts, setShouldFetchContacts] = useState(false);

  const [triggerFetchContacts, { data, isLoading, isFetching }] = useLazyGetContactsQuery();

  const isFetchComplete = !isLoading && !isFetching && data !== undefined;

  // const { data: dynamicColsData, isLoading: isDynamicColsLoading } = useGetDynamicColumnsQuery('contact');
  const { data: dynamicColsData, isLoading: isDynamicColsLoading } = useGetAllDynamicColumnsQuery({ scope: "contact", isStandard: false });
  const { data: dynamicFiltersData, isLoading: isDynamicFiltersLoading } = useGetDynamicFiltersQuery('contact');

  const getSavedParams = useCallback((): SavedTableParams | null => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;

    try {
      const parsed = JSON.parse(saved);
      if (parsed &&
        typeof parsed.page === 'number' &&
        typeof parsed.perPage === 'number' &&
        (parsed.sort === undefined || (
          typeof parsed.sort?.field === 'string' &&
          ['ASC', 'DESC'].includes(parsed.sort?.direction)
        )) &&
        (parsed.query === undefined || typeof parsed.query === 'string') &&
        parsed.filters && typeof parsed.filters === 'object' &&
        Array.isArray(parsed.visibleColumns)
      ) {

        return {
          page: parsed.page,
          perPage: parsed.perPage,
          sort: parsed.sort,
          query: parsed.query || '',
          filters: parsed.filters || {},
          visibleColumns: parsed.visibleColumns || []
        };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const fetchContacts = useCallback(async (
    params: SavedTableParams,
    onError?: (error: any) => void
  ) => {
    try {
      const result = await triggerFetchContacts({
        page: params.page,
        perPage: params.perPage,
        sort: params.sort,
        query: params.query,
        filters: params.filters,
        visibleColumns: params.visibleColumns,
      }).unwrap();

      return result;
    } catch (err) {
      showToast('Fetch Failed', 'Failed to fetch contacts. Please try again.', 'error');
      if (onError) onError(err);
      return null;
    }
  }, [triggerFetchContacts]);

  const saveParams = useCallback((params: SavedTableParams) => {
    const existing = localStorage.getItem(STORAGE_KEY);
    let merged = params;
    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        merged = { ...parsed, ...params };
      } catch {}
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  }, []);

  useEffect(() => {
    if (data) {
      setContacts(data.results ?? []);
      setTotalItems(data.total ?? 0);
    }
  }, [data]);

  useEffect(() => {
    if (dynamicColsData) {
      const transformed = dynamicColsData.map((col: any) => ({
        id: col.field || col.id || '',
        label: col.label || '',
        isSortable: col.isSortable ?? true,
      }));
      const idAddedCols = [
        { id: 'contact_id', label: 'contact_id', isSortable: false },
        ...transformed
      ];
      setColumns(idAddedCols);

      const savedParams = getSavedParams();
      const defaultParams: SavedTableParams = {
        page: 1,
        perPage: 10,
        filters: {},
        visibleColumns: idAddedCols
          .filter((col: any) => col.id?.startsWith('c.') || col.id === 'contact_id')
          .splice(0, 4)
          .map((col: any) => col.id),
        query: "",
      };

      // Intersect saved visibleColumns with currently available column ids so
      // we don't restore invalid/removed columns.
      const currentColumnIds = new Set(idAddedCols.map(c => c.id));

      let initialParams: SavedTableParams;
      if (savedParams) {
        const restoredVisible = (savedParams.visibleColumns || []).filter((c: string) => currentColumnIds.has(c));
        initialParams = {
          ...defaultParams,
          ...savedParams,
          visibleColumns: restoredVisible.length > 0 ? restoredVisible : defaultParams.visibleColumns,
        };
        // Only persist if we corrected/normalized the saved visible columns to valid ones
        if (JSON.stringify(restoredVisible) !== JSON.stringify(savedParams.visibleColumns)) {
          saveParams(initialParams);
        }
      } else {
        initialParams = defaultParams;
        // First time setup - persist defaults
        saveParams(initialParams);
      }

      setInitialFilters(initialParams);
      setShouldFetchContacts(true);
    }
  }, [dynamicColsData, getSavedParams, saveParams]);

  useEffect(() => {
    if (dynamicFiltersData?.length) {
      const fieldsToExclude = ['age', 'diabetes'];
      const filtered = dynamicFiltersData.filter(
        (item: MetadataField) =>
          !fieldsToExclude.some((excluded) =>
            item.field.toLowerCase().includes(excluded.toLowerCase())
          )
      );

      const transformed = filtered.map((col: any) => ({
        key: col.field || '',
        label: col.label || '',
        dataType: col.dataType || '',
        options: col.options ?? [],
      }));

      setFilterGroups(transformed);
    }
  }, [dynamicFiltersData]);

  useEffect(() => {
    if (shouldFetchContacts && initialFilters) {
      if (initialMount.current) {
        initialMount.current = false;
        fetchContacts(initialFilters);
      } else if (refreshTrigger) {
        fetchContacts(initialFilters);
      }
      setShouldFetchContacts(false);
    }
  }, [shouldFetchContacts, initialFilters, fetchContacts, refreshTrigger]);

  const handleFetchRequested = useCallback((params: SavedTableParams) => {
    saveParams(params);
    setInitialFilters(params);
    if (!params.visibleColumns.length) return;

    fetchContacts(params);
  }, [fetchContacts, saveParams]);

  // const confirmDelete = async () => {

  //   if (!deleteId) return;
  //   const numericId = Number(deleteId);
  //   setShowDeletePrompt(false);

  //   try {
  //     await contactService.deleteContact(numericId.toString());
  //     setContacts(prev => prev.filter(contact => contact.id !== numericId));
  //     setTotalItems(prev => prev - 1);
  //     showToast('Contact Deleted', 'The selected contact has been deleted.', 'success');

  //     const savedParams = getSavedParams();

  //     if (savedParams) {
  //       fetchContacts(savedParams);
  //     }
  //   } catch {
  //     showToast('Delete Failed', 'Failed to delete contact. Please try again.', 'error');
  //   }

  //   setDeleteId(null);
  // };


  const confirmDelete = async () => {
    // Stop if neither a deleteId nor selectedIds exist
    if (!deleteId && (!deleteIds || deleteIds.length === 0)) return;

    setShowDeletePrompt(false);

    try {
      if (deleteIds && deleteIds.length > 0) {
        // 🧩 Bulk delete case
        const response = await bulkDelete({
          recordIds: deleteIds,
          scope: 'contact',
        }).unwrap();

        setContacts(prev =>
          prev.filter(contact => !deleteIds.includes(contact.id))
        );
        setTotalItems(prev => prev - deleteIds.length);
      } else if (deleteId) {
        // 🧩 Single delete case
        const numericId = Number(deleteId);
        await contactService.deleteContact(numericId.toString());

        setContacts(prev =>
          prev.filter(contact => contact.id !== numericId)
        );
        setTotalItems(prev => prev - 1);
      }

      // ✅ Refresh and notify
      showToast('Contact Deleted', 'The selected contact has been deleted.', 'success');
      const savedParams = getSavedParams();
      if (savedParams) fetchContacts(savedParams);
      if (pendingCleanup) {
        pendingCleanup();
        setPendingCleanup(null);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('Delete Failed', 'Failed to delete contact. Please try again.', 'error');
    } finally {
      // ✅ Always clear delete state
      setDeleteId(null);
      setDeleteIds([]);
    }
  };


  const cancelDelete = () => {
    setShowDeletePrompt(false);
    setDeleteId(null);
  };

  const handleAction = useCallback(async (action: ActionType, id: number[] | string, state?: string | (() => void)) => {
    const numericId = Number(id);

    // if (action === 'delete') {
    //   if (id?.length > 0) {
    //     setDeleteIds(id)
    //   } else {

    //     setDeleteId(id);
    //   }
    //   setShowDeletePrompt(true);
    //   return;
    // }

    if (action === 'delete') {
      if (Array.isArray(id) && id.length > 0) {
        setDeleteIds(id);
      } else {
        setDeleteId(numericId);
      }
      setPendingCleanup(() => state);
      setShowDeletePrompt(true);
      return;
    }

    if (action === 'edit') {
      onEditContact(numericId, () => {
        const savedParams = getSavedParams();
        if (savedParams) {
          fetchContacts(savedParams);
        }
      });
    }

    if (action === 'view') {
      navigate(`${numericId}`);
    }
  }, [fetchContacts, onEditContact, navigate, getSavedParams]);
  const exportFiles = async (entity: string) => {
    setLoadingExport(true);
    const savedParams = getSavedParams();
    try {
      const token = getAccessToken(); // adjust if token is from Redux or cookies
      const queryString = new URLSearchParams();
      queryString.append('export', '1');
      if (savedParams?.visibleColumns?.length) {
        let columns = savedParams?.visibleColumns
          .filter((column: any) => column !== 'contact_id')
          .join(',');
        queryString.append('listingColumns', columns);
      }
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/${entity}?${queryString.toString()}`;
      const response = await fetch(apiUrl,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        showToast("Error", "Failed to export file", "error");
        return;
      }

      const blob = await response.blob();

      // Extract filename from content-disposition header (optional)
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
      const filename =
        filenameMatch?.[1] ||
        `export-${entity}.xlsx`;

      // Trigger file download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showToast("Success", "File downloaded successfully", "success");
    } catch (err) {
      console.error(err);
      showToast("Error", "Something went wrong while exporting", "error");
    } finally {
      setLoadingExport(false);
    }
  };
  return (
    <>
      <AMSDynamicListing
        showFilters
        showDateRangeFilter
        showActions
        routeKey="contacts"
        noDataTitle='No Contacts Found'
        noDataSubtitle='Your contact list is currently empty. Start adding new contacts to keep everything organized and easy to find.'
        filterGroups={filterGroups}
        title="Contacts Listing"
        subtitle="All contacts will show here"
        columns={columns}
        data={contacts}
        totalItems={totalItems}
        loading={isLoading || isDynamicColsLoading || isDynamicFiltersLoading}
        isFetching={isFetching || isDynamicFiltersLoading || isDynamicColsLoading}
        isFetchComplete={isFetchComplete}
        onAction={handleAction}
        onFetchRequested={handleFetchRequested}
        noTableAction={true}
        setInitialFilters={setInitialFilters}
        dynamicColumnsListing={true}
        skeletonHeight={'20px'}
        onExport={() => exportFiles('contacts')}
        loadingExport={loadingExport}
        hideActionColumn={true}
      />
      {showDeletePrompt && (
        <DeletePrompt onConfirm={confirmDelete} onCancel={cancelDelete} />
      )}
    </>
  );
};

export default ContactsListing;